import { Renderer, Program, Mesh, Triangle, Color } from 'ogl';

const vertexShader = `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

function buildFragmentShader(lineCount) {
    return `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform float iTime;
uniform vec3 iResolution;
uniform vec3 uColor;
uniform float uAmplitude;
uniform float uDistance;
uniform vec2 uMouse;
uniform float uSpeed;
uniform float uSpread;

#define PI 3.1415926538
#define LINE_COUNT ${lineCount}

const float u_line_width = 7.5;
const float u_line_blur = 15.0;

float Perlin2D(vec2 P) {
    vec2 Pi = floor(P);
    vec4 Pf_Pfmin1 = P.xyxy - vec4(Pi, Pi + 1.0);
    vec4 Pt = vec4(Pi.xy, Pi.xy + 1.0);
    Pt = Pt - floor(Pt * (1.0 / 71.0)) * 71.0;
    Pt += vec2(26.0, 161.0).xyxy;
    Pt *= Pt;
    Pt = Pt.xzxz * Pt.yyww;
    vec4 hash_x = fract(Pt * (1.0 / 951.135664));
    vec4 hash_y = fract(Pt * (1.0 / 642.949883));
    vec4 grad_x = hash_x - 0.49999;
    vec4 grad_y = hash_y - 0.49999;
    vec4 grad_results = inversesqrt(grad_x * grad_x + grad_y * grad_y)
        * (grad_x * Pf_Pfmin1.xzxz + grad_y * Pf_Pfmin1.yyww);
    grad_results *= 1.4142135623730950;
    vec2 blend = Pf_Pfmin1.xy * Pf_Pfmin1.xy * Pf_Pfmin1.xy
               * (Pf_Pfmin1.xy * (Pf_Pfmin1.xy * 6.0 - 15.0) + 10.0);
    vec4 blend2 = vec4(blend, vec2(1.0 - blend));
    return dot(grad_results, blend2.zxzx * blend2.wwyy);
}

float pixel(float count, vec2 resolution) {
    return (1.0 / max(resolution.x, resolution.y)) * count;
}

float lineFn(vec2 st, float width, float perc, float offset, vec2 mouse, float time, float amplitude, float distance) {
    float split_offset = (perc * 0.4);
    float split_point = 0.1 + split_offset;

    float amplitude_normal = 0.5 + (smoothstep(0.0, 0.6, st.x) * 0.5);
    float amplitude_strength = 0.5;
    float finalAmplitude = amplitude_normal * amplitude_strength
                           * amplitude * (1.0 + (mouse.y - 0.5) * 0.2);

    float time_scaled = time / 10.0 + (mouse.x - 0.5) * 1.0;
    float blur = smoothstep(split_point, split_point + 0.05, st.x) * perc;

    float xnoise = mix(
        Perlin2D(vec2(st.x - time_scaled, perc) * 2.5),
        Perlin2D(vec2(st.x - time_scaled * 0.8, perc + time_scaled * 0.5) * 3.5) / 1.5,
        st.x * 0.3
    );

    float y = 0.5 + (perc - 0.5) * distance + xnoise / 2.0 * finalAmplitude;

    float line_start = smoothstep(
        y + (width / 2.0) + (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        y,
        st.y
    );

    float line_end = smoothstep(
        y,
        y - (width / 2.0) - (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        st.y
    );

    return clamp(
        (line_start - line_end) * (1.0 - smoothstep(0.0, 1.0, pow(perc, 0.3))),
        0.0,
        1.0
    );
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
    vec2 uv = fragCoord / iResolution.xy;

    float line_strength = 1.0;

    for (int i = 0; i < LINE_COUNT; i++) {
        float p = (float(i) / float(LINE_COUNT)) * uSpread;
        line_strength *= (1.0 - lineFn(
            uv,
            u_line_width * pixel(1.0, iResolution.xy) * (1.0 - p),
            p,
            (PI * 1.0) * p,
            uMouse,
            iTime * uSpeed,
            uAmplitude,
            uDistance
        ));
    }

    float colorVal = 1.0 - line_strength;
    fragColor = vec4(uColor * colorVal, colorVal);
}

void main() {
    mainImage(gl_FragColor, gl_FragCoord.xy);
}
`;
}

export function initThreadsBackground(container, options = {}) {
    if (!container) {
        console.warn('Threads background: Container element not found');
        return null;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return null;
    }

    const {
        color = [1, 1, 1],
        amplitude = 1,
        distance = 0,
        opacity = 1,
        lineCount = 25,
        speed = 1,
        spread = 1
    } = options;

    let renderer;
    try {
        renderer = new Renderer({ alpha: true });
    } catch (error) {
        console.warn('Threads background: WebGL not supported or failed to initialize', error);
        return null;
    }

    const gl = renderer.gl;

    if (!gl) {
        console.warn('Threads background: WebGL context not available');
        return null;
    }
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    gl.canvas.style.position = 'absolute';
    gl.canvas.style.top = '0';
    gl.canvas.style.left = '0';
    gl.canvas.style.pointerEvents = 'none';
    gl.canvas.style.visibility = 'hidden';

    if (opacity < 1) {
        gl.canvas.style.opacity = opacity;
    }

    container.appendChild(gl.canvas);

    const geometry = new Triangle(gl);

    let program;
    try {
        program = new Program(gl, {
            vertex: vertexShader,
            fragment: buildFragmentShader(lineCount),
            uniforms: {
                iTime: { value: 0 },
                iResolution: {
                    value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
                },
                uColor: { value: new Color(...color) },
                uAmplitude: { value: amplitude },
                uDistance: { value: distance },
                uMouse: { value: new Float32Array([0.5, 0.5]) },
                uSpeed: { value: speed },
                uSpread: { value: spread }
            }
        });
    } catch (error) {
        console.warn('Threads background: Failed to create shader program', error);
        if (container.contains(gl.canvas)) {
            container.removeChild(gl.canvas);
        }
        return null;
    }

    const mesh = new Mesh(gl, { geometry, program });

    function applyResize() {
        const { clientWidth, clientHeight } = container;
        const isMobile = window.innerWidth < 768;
        const maxRes = isMobile ? 540 : 920;
        const scale = Math.min(window.devicePixelRatio || 1, 1.5);
        const width = Math.min(clientWidth * scale, maxRes);
        const height = Math.min(clientHeight * scale, maxRes);
        renderer.setSize(width, height);
        gl.canvas.width = width;
        gl.canvas.height = height;
        gl.canvas.style.width = clientWidth + 'px';
        gl.canvas.style.height = clientHeight + 'px';
        gl.canvas.style.visibility = 'visible';
        program.uniforms.iResolution.value.r = width;
        program.uniforms.iResolution.value.g = height;
        program.uniforms.iResolution.value.b = width / height;
    }

    let resizeTimer;
    function resize() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyResize, 100);
    }
    window.addEventListener('resize', resize);
    applyResize();

    let animationFrameId;
    let hasShutDown = false;
    let isVisible = true;

    const observer = new IntersectionObserver(
        (entries) => { isVisible = entries[0].isIntersecting; },
        { threshold: 0 }
    );
    observer.observe(container);

    const gracefulShutdown = () => {
        if (hasShutDown) {
            return;
        }
        hasShutDown = true;
        observer.disconnect();
        try { if (animationFrameId) cancelAnimationFrame(animationFrameId); } catch (e) {}
        try { window.removeEventListener('resize', resize); } catch (e) {}
        try {
            gl.canvas.style.transition = 'opacity 50ms linear';
            gl.canvas.style.opacity = '0';
        } catch (e) {}
        setTimeout(() => {
            try { if (container.contains(gl.canvas)) container.removeChild(gl.canvas); } catch (e) {}
            try {
                const loseCtxExt = gl.getExtension('WEBGL_lose_context');
                if (loseCtxExt) loseCtxExt.loseContext();
            } catch (e) {}
        }, 140);
    };

    const onBeforeUnload = () => gracefulShutdown();
    window.addEventListener('beforeunload', onBeforeUnload, { once: true });

    function update(t) {
        animationFrameId = requestAnimationFrame(update);
        if (!isVisible) {
            return;
        }
        program.uniforms.iTime.value = t * 0.001;
        renderer.render({ scene: mesh });
    }
    animationFrameId = requestAnimationFrame(update);

    return function cleanup() {
        window.removeEventListener('beforeunload', onBeforeUnload);
        gracefulShutdown();
    };
}

document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.threads-bg-layer');

    containers.forEach(container => {
        try {
            const isMobile = window.innerWidth < 768;
            const amplitude = parseFloat(container.dataset.amplitude) || 1;
            const distance = parseFloat(container.dataset.distance) || 0;
            const opacity = parseFloat(container.dataset.opacity) || 1;
            const baseLineCount = parseInt(container.dataset.lineCount) || 25;
            const lineCount = isMobile ? Math.floor(baseLineCount * 0.6) : baseLineCount;
            const speed = parseFloat(container.dataset.speed) || 1;
            const spread = parseFloat(container.dataset.spread) || 1;
            const colorStr = container.dataset.color || '1,1,1';
            const color = colorStr.split(',').map(v => parseFloat(v.trim()));

            const cleanup = initThreadsBackground(container, {
                color,
                amplitude,
                distance,
                opacity,
                lineCount,
                speed,
                spread
            });

            if (!cleanup) {
                console.warn('Threads background: Failed to initialize for container', container);
            }
        } catch (error) {
            console.error('Threads background: Error initializing container', error);
        }
    });
});
